import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  buildCategoryTree,
  normalizeCategoriesResponse,
  recordRecentId,
  type Category,
  type CategoryKind,
} from "@/components/categories/categoryHelpers";
import {
  EntryPickerOverlay,
  PickerRow,
  PickerSectionHeading,
} from "./EntryPickerOverlay";
import {
  categoryColor,
  childColor,
  searchCategories,
  selectableCategories,
  topFrequentCategories,
} from "./pickerHelpers";
import type { SuggestionTemplate } from "@/lib/suggestions/service";
import { apiFetch } from "@/lib/api/client";
import { apiUrl } from "@/lib/api/config";

interface CategoryPickerSheetProps {
  open: boolean;
  kind: CategoryKind;
  value: string | null;
  onSelect: (id: string) => void;
  onClose: () => void;
  /** Ranked history, already fetched by the entry screen — not re-fetched. */
  suggestions: SuggestionTemplate[];
}

async function fetchCategories(kind: CategoryKind): Promise<Category[]> {
  // includeArchived=true so an already-assigned archived category can still be
  // resolved and shown as selected; `selectableCategories` still hides every
  // other archived row.
  const res = await apiFetch(apiUrl("categories", "", { kind, includeArchived: "true" }));
  if (!res.ok) throw new Error("Failed to load categories");
  return normalizeCategoriesResponse(await res.json());
}

/**
 * Full-screen category picker, two levels deep.
 *
 * Replaces the inline chip grid on mobile. The chip grid rendered a heading
 * per root category, and since every seeded category is flat that produced an
 * ~840px alternating heading/chip ladder inside the entry sheet — the single
 * biggest reason the sheet did not fit on a phone.
 *
 * The two-level rule, which is what stops that bug recurring:
 *   • a category WITH children pushes a subcategory screen (with a GENERAL
 *     row so the parent itself is still selectable);
 *   • a category WITHOUT children is selected on tap and the picker closes.
 * No heading is ever rendered for a childless root.
 */
export function CategoryPickerSheet({
  open,
  kind,
  value,
  onSelect,
  onClose,
  suggestions,
}: CategoryPickerSheetProps) {
  const { data: categories = [], isLoading } = useQuery({
    // Same key the inline CategoryPicker uses — one fetch serves both.
    queryKey: ["categories", kind],
    queryFn: () => fetchCategories(kind),
    enabled: open,
  });

  const [parentId, setParentId] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);

  // Every open starts at the root with no query — a picker that reopens
  // mid-drill from a previous entry is disorienting.
  useEffect(() => {
    if (!open) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- deliberate open-time reset of transient navigation state
    setParentId(null);
    setQuery("");
    setSearchOpen(false);
  }, [open]);

  const selectable = useMemo(
    () => selectableCategories(categories, value),
    [categories, value],
  );
  const tree = useMemo(() => buildCategoryTree(selectable), [selectable]);
  const frequent = useMemo(
    () => topFrequentCategories(suggestions, selectable, 4),
    [suggestions, selectable],
  );

  const parent = parentId ? selectable.find((c) => c.id === parentId) ?? null : null;
  const parentNode = parent ? tree.find((n) => n.id === parent.id) ?? null : null;

  function choose(id: string) {
    onSelect(id);
    recordRecentId(kind, id);
    onClose();
  }

  const trimmedQuery = query.trim();
  const searchResults = useMemo(
    () => (trimmedQuery ? searchCategories(selectable, trimmedQuery) : []),
    [selectable, trimmedQuery],
  );

  return (
    <EntryPickerOverlay
      open={open}
      title={parent ? parent.name : "Category"}
      onBack={() => (parent ? setParentId(null) : onClose())}
      query={query}
      onQueryChange={setQuery}
      searchOpen={searchOpen}
      onSearchOpenChange={setSearchOpen}
      settingsHref="/admin/categories"
      settingsLabel="Manage categories"
    >
      {isLoading && (
        <p className="px-4 py-6 text-sm text-[var(--cp-ink-3)]">Loading categories…</p>
      )}

      {!isLoading && trimmedQuery !== "" && (
        <>
          {searchResults.length === 0 ? (
            <p className="px-4 py-6 text-sm text-[var(--cp-ink-3)]">No matching categories</p>
          ) : (
            searchResults.map((c) => {
              const owner = c.parentId ? selectable.find((p) => p.id === c.parentId) : null;
              return (
                <PickerRow
                  key={c.id}
                  color={owner ? childColor(owner, c) : categoryColor(c)}
                  label={c.name}
                  sublabel={owner?.name}
                  selected={c.id === value}
                  onClick={() => choose(c.id)}
                />
              );
            })
          )}
        </>
      )}

      {/* Subcategory screen: GENERAL (the parent itself) + SUBCATEGORIES. */}
      {!isLoading && trimmedQuery === "" && parentNode && (
        <>
          <PickerSectionHeading>General</PickerSectionHeading>
          <PickerRow
            color={categoryColor(parentNode)}
            label={parentNode.name}
            selected={parentNode.id === value}
            onClick={() => choose(parentNode.id)}
          />
          <PickerSectionHeading>Subcategories</PickerSectionHeading>
          {parentNode.children.map((c) => (
            <PickerRow
              key={c.id}
              color={childColor(parentNode, c)}
              label={c.name}
              selected={c.id === value}
              onClick={() => choose(c.id)}
            />
          ))}
        </>
      )}

      {/* Root screen. */}
      {!isLoading && trimmedQuery === "" && !parentNode && (
        <>
          {/* Omitted entirely when there is no history — an empty band is
              worse than no band, and this DB starts with zero entries. */}
          {frequent.length > 0 && (
            <>
              <PickerSectionHeading>Most frequent</PickerSectionHeading>
              <div className="grid grid-cols-4 gap-2 px-4 pb-2">
                {frequent.map((c) => (
                  <button
                    key={`freq-${c.id}`}
                    type="button"
                    onClick={() => choose(c.id)}
                    aria-label={c.name}
                    className="flex min-h-10 flex-col items-center gap-1.5 rounded-lg py-1 transition-colors hover:bg-[var(--cp-surface-2)]"
                  >
                    <span
                      className="flex h-14 w-14 items-center justify-center rounded-full text-lg font-bold text-white"
                      style={{ backgroundColor: categoryColor(c) }}
                      aria-hidden
                    >
                      {c.name.trim().charAt(0).toUpperCase()}
                    </span>
                    <span className="line-clamp-2 text-center text-[11px] leading-tight text-[var(--cp-ink-2)]">
                      {c.name}
                    </span>
                  </button>
                ))}
              </div>
            </>
          )}

          <PickerSectionHeading>All categories</PickerSectionHeading>
          {tree.length === 0 && (
            <p className="px-4 py-6 text-sm text-[var(--cp-ink-3)]">No categories yet.</p>
          )}
          {tree.map((node) => (
            <PickerRow
              key={node.id}
              color={categoryColor(node)}
              label={node.name}
              sublabel={
                node.children.length > 0
                  ? `${node.children.length} subcategor${node.children.length === 1 ? "y" : "ies"}`
                  : undefined
              }
              selected={node.id === value}
              onClick={() =>
                node.children.length > 0 ? setParentId(node.id) : choose(node.id)
              }
            />
          ))}
        </>
      )}
    </EntryPickerOverlay>
  );
}
